import React, { useRef, useEffect } from 'react';
import { COLORS } from '../../styles/tokens';

const SHEET_VH = 0.88;
// Synlig høyde i peek-tilstand. Satt høyt nok til at toppen av arket (header)
// alltid er godt synlig og lett å ta tak i for en sjåfør på farten — arket
// skal aldri kunne dras så langt ned at det føles "helt borte".
const PEEK_PX  = 140;
// Laveste nivå: bare håndtaket stikker opp over bunnmenyen, så kartet er nesten fritt
// men arket fortsatt kan gripes og dras opp igjen.
const LOW_PX   = 36;
// Nok til ett åpnet oppdrag med budskjemaet (kort, oppdragsgiver, adresser, beløp og knapp), uten å dekke hele kartet
const MINI_PX  = 500;

// Synlig høyde (px) i «mini»-tilstand for et ark med gitt reserve nederst
export function miniVisiblePx(inset = 0, viewportH = window.innerHeight) {
  const sheetH = viewportH * SHEET_VH - inset;
  return Math.round(Math.min(MINI_PX, sheetH - 120));
}


// Velger hvilket nivå arket skal snappe til når fingeren slippes på posisjon y (px fra toppen).
// order: nivåene fra øverst til nederst; yOf(name): posisjonen til hvert nivå.
// Ligger y nærmere enn terskelen (px) det nåværende nivået, blir arket der. Ellers velges nærmeste nivå
// i dra-retningen, slik at ett langt dra nedover går helt ned selv om det ligger flere nivåer imellom.
export function pickSnap({ order, current, y, yOf, threshold = 50 }) {
  const currentY = yOf(current);
  const dy = y - currentY;
  if (Math.abs(dy) < threshold) return current;
  const idx = Math.max(0, order.indexOf(current));
  const candidates = dy < 0 ? order.slice(0, idx) : order.slice(idx + 1);
  if (candidates.length === 0) return current;
  return candidates.reduce((best, name) => (Math.abs(yOf(name) - y) < Math.abs(yOf(best) - y) ? name : best), candidates[0]);
}

// header: vises øverst og fungerer som dragflate. Siden arket aldri forsvinner helt,
// trengs det ikke et eget håndtak-symbol — hele toppen kan dras.
// inset: høyde (px) nederst som er opptatt av noe annet, f.eks. bunnmenyen.
export default function BottomSheet({ header, children, inset = 0, openKey = null }) {
  const sheetRef      = useRef(null);
  const dragRef       = useRef(null);
  const snapNameRef   = useRef('peek');
  const contentRef    = useRef(null);
  const contentDragRef = useRef(null); // null | { startY, startT, direction }

  function getSnapY(name) {
    const h = window.innerHeight * SHEET_VH - inset;
    if (name === 'full') return 0;
    if (name === 'half') return h * 0.50;
    if (name === 'low') return h - LOW_PX;
    return h - PEEK_PX;
  }

  function snapTo(name, animated = true) {
    const el = sheetRef.current;
    if (!el) return;
    snapNameRef.current = name;
    el.style.transition = animated
      ? 'transform 0.35s cubic-bezier(0.32,0.72,0,1)'
      : 'none';
    el.style.transform = `translateY(${getSnapY(name)}px)`;
  }

  useEffect(() => { snapTo('half', false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Menyen under kan endre høyde (safe-area, rotasjon): hold arket i samme posisjon relativt til den
  useEffect(() => { snapTo(snapNameRef.current, false); }, [inset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Et oppdrag som åpnes folder seg ut i lista og dytter innholdet nedover; arket beholder nivået sitt,
  // bortsett fra at det løftes fra peek til halvt åpent så oppdraget er synlig.
  useEffect(() => {
    if (openKey != null && snapNameRef.current === 'peek') snapTo('half');
  }, [openKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function currentTranslateY() {
    const el = sheetRef.current;
    if (!el) return 0;
    return new DOMMatrix(window.getComputedStyle(el).transform).m42;
  }

  function resolveSnap(y) {
    return pickSnap({ order: ['full', 'half', 'peek', 'low'], current: snapNameRef.current, y, yOf: getSnapY });
  }

  // ── Header drag (top bar) ──────────────────────────────────────────────────
  function onTouchStart(e) {
    sheetRef.current.style.transition = 'none';
    dragRef.current = {
      startY: e.touches[0].clientY,
      startT: currentTranslateY(),
    };
  }

  function onTouchMove(e) {
    if (!dragRef.current) return;
    const dy = e.touches[0].clientY - dragRef.current.startY;
    // Klemmer til 'low' som gulv — arket skal aldri kunne dras lenger ned enn
    // det, slik at håndtaket alltid forblir synlig og lett å ta tak i igjen.
    const y = Math.min(getSnapY('low'), Math.max(0, dragRef.current.startT + dy));
    sheetRef.current.style.transform = `translateY(${y}px)`;
  }

  function onTouchEnd() {
    if (!dragRef.current) return;
    snapTo(resolveSnap(currentTranslateY()));
    dragRef.current = null;
  }

  // ── Content drag (list area) ───────────────────────────────────────────────
  function getScrollTop() {
    if (!contentRef.current) return 0;
    for (const el of contentRef.current.querySelectorAll('*')) {
      const ov = window.getComputedStyle(el).overflowY;
      if ((ov === 'auto' || ov === 'scroll') && el.scrollHeight > el.clientHeight) {
        return el.scrollTop;
      }
    }
    return 0;
  }

  function onContentTouchStart(e) {
    contentDragRef.current = {
      startY:    e.touches[0].clientY,
      startT:    currentTranslateY(),
      direction: null,   // 'up' | 'down', locked on first significant move
      takeover:  false,  // true = we are moving the sheet
    };
  }

  // Non-passive touchmove so we can call preventDefault (registered via useEffect)
  function onContentTouchMoveNative(e) {
    if (!contentDragRef.current) return;
    const dy = e.touches[0].clientY - contentDragRef.current.startY;

    // Lock direction on first significant movement
    if (contentDragRef.current.direction === null) {
      // Ikke preventDefault her: et trykk med litt fingerbevegelse skal fortsatt gi klikk på knapper.
      // Nettleseren starter ikke rulling før en større bevegelse, så retningen låses i tide.
      if (Math.abs(dy) < 5) return;
      const dir = dy > 0 ? 'down' : 'up';
      const isFull      = snapNameRef.current === 'full';
      const scrollTop   = getScrollTop();
      // Decide whether we take over the sheet or let the list scroll
      const takeover =
        (dir === 'up'   && !isFull)           ||  // open sheet fully
        (dir === 'down' && isFull && scrollTop === 0) ||  // collapse from top
        (dir === 'down' && !isFull);              // collapse from half/peek
      contentDragRef.current.direction = dir;
      contentDragRef.current.takeover  = takeover;
      if (takeover) sheetRef.current.style.transition = 'none';
    }

    if (!contentDragRef.current.takeover) return; // let native scroll handle it

    e.preventDefault();
    // Samme gulv-klemming som onTouchMove — se kommentar der.
    const y = Math.min(getSnapY('low'), Math.max(0, contentDragRef.current.startT + dy));
    sheetRef.current.style.transform = `translateY(${y}px)`;
  }

  function onContentTouchEnd() {
    if (!contentDragRef.current) return;
    if (contentDragRef.current.takeover) {
      snapTo(resolveSnap(currentTranslateY()));
    }
    contentDragRef.current = null;
  }

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener('touchmove', onContentTouchMoveNative, { passive: false });
    return () => el.removeEventListener('touchmove', onContentTouchMoveNative);
  }); // re-bind each render so closure stays fresh

  return (
    <div
      ref={sheetRef}
      style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: `calc(${SHEET_VH * 100}vh - ${inset}px)`,
        background: COLORS.white,
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
        zIndex: 100,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        willChange: 'transform',
      }}
    >
      {/* Header — dragflate. Trykk på knapper i headeren gir fortsatt vanlige klikk. */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          flexShrink: 0,
          touchAction: 'none',
          userSelect: 'none',
          background: COLORS.white,
        }}
      >
        {header}
      </div>

      {/* Scrollable content — also draggable when not fully open */}
      <div
        ref={contentRef}
        onTouchStart={onContentTouchStart}
        onTouchEnd={onContentTouchEnd}
        style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {children}
      </div>
    </div>
  );
}
